/**
 * Agent Review API service.
 */

import axios from "axios";
import { apiRequestWithHeaders, del, get, post } from "./client";
import type {
  AgentRun,
  AgentStep,
  ArtefactRatings,
  CheckAnswer,
  PhaseCriteria,
  PhaseRatings,
  Rag,
  ReviewComparison,
  ReviewRecord,
} from "@/components/ops/agent-review/types";
import { firstOrchestrationStep, finishStep } from "@/components/ops/agent-review/phases";

const BASE = "/agentic-v2/agent-review";
const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const LOA_EXPORT_SLUGS: Record<string, string> = {
  octopus_generate_loas: "loa_generate",
  octopus_rerun_loas: "loa_rerun",
};

function catalogueSlugForExport(catalogueKey: string): string {
  const key = catalogueKey.trim();
  if (LOA_EXPORT_SLUGS[key]) {
    return LOA_EXPORT_SLUGS[key];
  }
  return key.replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 48);
}

function formatExportStamp(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return (
    `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}` +
    `_${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}`
  );
}

export function buildAgentReviewExportFilename(
  catalogueKey: string,
  sessionName?: string | null,
  exportedAt = new Date(),
): string {
  const slug = catalogueSlugForExport(catalogueKey);
  const stamp = formatExportStamp(exportedAt);
  if (sessionName?.trim()) {
    const safeSession = sessionName.trim().replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 32);
    return `agent_review_${slug}_${safeSession}_${stamp}.xlsx`;
  }
  return `agent_review_${slug}_${stamp}.xlsx`;
}

export function buildCalibrationExportFilename(
  catalogueKey: string,
  sessionName?: string | null,
  exportedAt = new Date(),
): string {
  const slug = catalogueSlugForExport(catalogueKey);
  const stamp = formatExportStamp(exportedAt);
  if (sessionName?.trim()) {
    const safeSession = sessionName.trim().replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 32);
    return `agent_review_calibration_${slug}_${safeSession}_${stamp}.xlsx`;
  }
  return `agent_review_calibration_${slug}_${stamp}.xlsx`;
}

function filenameFromContentDisposition(disposition: string | undefined): string | null {
  if (!disposition) return null;
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }
  const match = disposition.match(/filename="([^"]+)"/i) ?? disposition.match(/filename=([^;]+)/i);
  return match?.[1]?.trim() ?? null;
}

function isLoaCatalogueKey(catalogueKey: string | null | undefined): boolean {
  if (!catalogueKey) return false;
  const key = catalogueKey.toLowerCase();
  return key.includes("loa") || key.includes("octopus_generate_loas") || key.includes("octopus_rerun_loas");
}

/** Match server-side batching (3 concurrent Ace runs) and auth middleware (1h cap). */
export function aceComparisonExportTimeoutMs(
  reviewCount: number,
  profileCount: number,
  catalogueKey?: string | null,
): number {
  const pairs = Math.max(1, reviewCount) * Math.max(1, profileCount);
  const perPairMs = isLoaCatalogueKey(catalogueKey) ? 180_000 : 90_000;
  const parallelBatches = Math.ceil(pairs / 3);
  return Math.min(3_600_000, 120_000 + parallelBatches * perPairMs);
}

/** Sequential Ace runs for in-app stability test (execution time only, not queue wait). */
export function aceStabilityTestTimeoutMs(
  iterations: number,
  catalogueKey?: string | null,
): number {
  const runs = Math.max(1, iterations);
  const perRunMs = isLoaCatalogueKey(catalogueKey) ? 120_000 : 90_000;
  return Math.min(3_600_000, 120_000 + runs * perRunMs);
}

export function aceRateTimeoutMs(artefactCount = 0): number {
  return Math.min(600_000, 180_000 + artefactCount * 45_000);
}

export async function parseApiErrorMessage(err: unknown, fallback: string): Promise<string> {
  if (!axios.isAxiosError(err) || !err.response?.data) {
    return err instanceof Error ? err.message : fallback;
  }

  const data = err.response.data;
  if (typeof data === "string") {
    if (err.response.status === 404 && data.toLowerCase() === "not found") {
      return "Export endpoint not found — restart the API to load the latest code.";
    }
    return data;
  }

  if (typeof data === "object" && data !== null && "detail" in data) {
    const detail = (data as { detail?: unknown }).detail;
    if (typeof detail === "string") {
      if (err.response.status === 404 && detail.toLowerCase() === "not found") {
        return "Export endpoint not found — restart the API to load the latest code.";
      }
      return detail;
    }
    if (Array.isArray(detail) && detail[0] && typeof detail[0] === "object" && "msg" in detail[0]) {
      return String((detail[0] as { msg?: string }).msg || fallback);
    }
  }

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text) as { detail?: string | Array<{ msg?: string }> };
      if (typeof parsed.detail === "string") {
        return parsed.detail;
      }
      if (Array.isArray(parsed.detail) && parsed.detail[0]?.msg) {
        return parsed.detail[0].msg;
      }
    } catch {
      // Fall through to status-based message.
    }
  }

  if (err.response.status === 404) {
    return "Export endpoint not found — restart the API to load the latest code.";
  }

  if (err.response.status === 504 || err.response.status === 408) {
    return "Export timed out — LOA Ace runs can take several minutes each. Try fewer cases or profiles, then retry.";
  }

  return err.message || fallback;
}

export interface RunListItem {
  run_id: string;
  source: "live" | "seed";
  run_kind?: "task" | "chat";
  task_name: string;
  task_summary: string;
  catalogue_key?: string | null;
  client_id?: string | null;
  occurred_at?: string | null;
  status?: string | null;
  step_count: number;
  already_reviewed: boolean;
  label?: string | null;
  subject_label?: string;
  summary_snippet?: string;
  infrastructure_failure?: boolean;
  infrastructure_failure_reason?: string | null;
  conversation_id?: string | null;
  user_name?: string;
}

export interface TaskTypeOption {
  catalogue_key: string;
  display_name: string;
  run_kind?: "task" | "chat";
}

export interface ImportCandidate {
  interaction_id: string;
  task_id: string;
  catalogue_key: string;
  client_id?: string | null;
  subject_label?: string;
  completed_at?: string | null;
  step_count: number;
  summary_snippet: string;
  disposition?: string | null;
  already_in_queue: boolean;
}

export interface ChatImportCandidate {
  conversation_id: string;
  catalogue_key: string;
  client_id?: string | null;
  subject_label?: string;
  agent_name?: string;
  user_name?: string;
  completed_at?: string | null;
  message_count: number;
  interaction_count: number;
  summary_snippet: string;
  already_in_queue: boolean;
}

export interface ImportLookupCandidate extends ImportCandidate {
  importable: boolean;
  block_reason?: string | null;
  interaction_status?: string | null;
}

export interface ImportLookupResult {
  lookup_type: "task_id" | "interaction_id";
  query_id: string;
  candidates: ImportLookupCandidate[];
}

export interface ImportSkip {
  id: string;
  reason: string;
}

export interface ImportResult {
  imported_count: number;
  run_ids: string[];
  skipped: ImportSkip[];
}

export interface ClearQueueResult {
  live_removed: number;
  samples_removed: number;
  reviews_removed: number;
}

export interface RagCounts {
  green: number;
  amber: number;
  red: number;
}

export interface PhaseAgreementStats {
  compared: number;
  agreed: number;
  disagreed: number;
}

export interface AnswerPairCount {
  human_answer: CheckAnswer;
  ace_answer: CheckAnswer;
  count: number;
}

export interface CalibrationCaseRef {
  review_id: string;
  run_id: string;
  subject_label?: string;
  artifact_id?: string;
  artifact_title?: string;
}

export interface CalibrationPriority {
  phase: "orchestration" | "output" | "artefact";
  check_id: string;
  check_label: string;
  severity?: "hard" | "soft" | null;
  generic?: boolean;
  dispute_count: number;
  open_dispute_count: number;
  resolved_dispute_count: number;
  dominant_pattern: string;
  answer_pairs: AnswerPairCount[];
  ace_reason_samples: string[];
  calibration_hint: string;
  affected_cases: CalibrationCaseRef[];
}

export interface PhaseRagDisparity {
  phase: "orchestration" | "output";
  human_rag: Rag;
  ace_rag: Rag;
  count: number;
  affected_cases: CalibrationCaseRef[];
}

export interface CheckDisagreementSummary {
  phase: "orchestration" | "output" | "artefact";
  check_id: string;
  check_label: string;
  count: number;
}

export interface MisalignedReviewItem {
  review_id: string;
  run_id: string;
  interaction_id?: string | null;
  task_id?: string | null;
  subject_label?: string;
  reviewer_id: string;
  submitted_at?: string | null;
  source: "live" | "seed";
  orch_human_rag: Rag | null;
  orch_ace_rag: Rag | null;
  orch_agrees: boolean | null;
  out_human_rag: Rag | null;
  out_ace_rag: Rag | null;
  out_agrees: boolean | null;
  check_disagreement_count: number;
  misaligned: boolean;
}

export type CalibrationResolutionOutcome = "human_correct" | "ace_correct" | "resolved";

export interface CheckDisagreementDetail {
  review_id: string;
  run_id: string;
  subject_label?: string;
  phase: "orchestration" | "output" | "artefact";
  check_id: string;
  check_label: string;
  artifact_id?: string;
  artifact_title?: string;
  human_answer: CheckAnswer;
  ace_answer: CheckAnswer;
  human_comment?: string;
  ace_reason?: string;
  resolution_id?: string | null;
  resolution_outcome?: CalibrationResolutionOutcome | null;
  resolution_note?: string;
  resolved_by?: string;
  resolved_at?: string | null;
}

export interface CalibrationResolutionIn {
  catalogue_key: string;
  session_id?: string | null;
  review_id: string;
  run_id: string;
  phase: "orchestration" | "output" | "artefact";
  check_id: string;
  check_label: string;
  artifact_id?: string;
  human_answer: CheckAnswer;
  ace_answer: CheckAnswer;
  outcome: CalibrationResolutionOutcome;
  note?: string;
}

export interface CalibrationResolutionClearIn {
  review_id: string;
  phase: "orchestration" | "output" | "artefact";
  check_id: string;
  artifact_id?: string;
}

export interface CalibrationResolutionRecord extends CalibrationResolutionIn {
  resolution_id: string;
  note: string;
  resolved_by: string;
  resolved_at: string;
}

export interface ReviewAnalysisResult {
  catalogue_key: string;
  task_name: string;
  total_reviews: number;
  ace_rated_count: number;
  pending_ace_count: number;
  orchestration_agreement: PhaseAgreementStats;
  output_agreement: PhaseAgreementStats;
  artefact_agreement: PhaseAgreementStats;
  human_orch_rags: RagCounts;
  human_out_rags: RagCounts;
  calibration_headline: string;
  calibration_priorities: CalibrationPriority[];
  phase_rag_disparities: PhaseRagDisparity[];
  top_check_disagreements: CheckDisagreementSummary[];
  misaligned_reviews: MisalignedReviewItem[];
  check_disagreements: CheckDisagreementDetail[];
  open_dispute_count: number;
  resolved_dispute_count: number;
}

export interface AceRatePendingFailure {
  review_id: string;
  run_id: string;
  subject_label: string;
  error: string;
}

export interface BatchAceRateResult {
  catalogue_key: string;
  session_id?: string | null;
  pending_count: number;
  rated_count: number;
  failed_count: number;
  failures: AceRatePendingFailure[];
}

export interface ReviewVersionSnapshot {
  ratings: ReviewRecord["ratings"];
  ace_rating?: ReviewRecord["ace_rating"];
}

export interface ReviewVersionEntry {
  version_id: string;
  review_id: string;
  run_id: string;
  version_number: number;
  change_type: "human_submit" | "ace_rate";
  actor_id: string;
  recorded_at: string;
  criteria_version_id?: string | null;
  criteria_version_label?: string | null;
  note?: string;
  snapshot: ReviewVersionSnapshot;
}

export interface CriteriaVersionRecord {
  criteria_version_id: string;
  catalogue_key: string;
  version_number: number;
  label: string;
  created_at: string;
  created_by: string;
  is_current: boolean;
}

export interface MoveRunsToSessionResult {
  moved_count: number;
  skipped: ImportSkip[];
}

export interface ReviewSessionRecord {
  session_id: string;
  name: string;
  catalogue_key: string;
  client_id?: string | null;
  created_at: string;
  created_by: string;
  is_legacy?: boolean;
}

export interface DeleteSessionResult {
  session_id: string;
  live_removed: number;
  samples_removed: number;
  reviews_removed: number;
  ace_trials_removed: number;
  review_versions_removed: number;
}

export interface ExportResultsOptions {
  include_summary: boolean;
  include_reviews: boolean;
  include_checks: boolean;
  include_loa: boolean;
}

export interface AceComparisonExportRequest {
  runIds: string[];
  aceProfiles: AceProfile[];
  force?: boolean;
  includeTrialChecks?: boolean;
}

export interface AceComparisonExportTaskResponse {
  task_id: string;
  status: string;
  message: string;
}

export interface AceComparisonExportTaskStatus {
  task_id: string;
  status: string;
  progress?: number | null;
  status_message?: string | null;
  error?: string | null;
  filename?: string | null;
  ready: boolean;
}

export interface AceStabilityTestRequest {
  runId: string;
  aceProfile: AceProfile;
  iterations: number;
}

export interface AceStabilityCheckVariance {
  phase: string;
  artifact_id?: string | null;
  artifact_title?: string | null;
  check_id: string;
  check_label: string;
  severity: string;
  iterations: number;
  pass_count: number;
  fail_count: number;
  na_count: number;
  other_count: number;
  unique_answers: number;
  mode_answer: string;
  stable: boolean;
}

export interface AceStabilityPhaseVariance {
  phase: string;
  artifact_id?: string | null;
  artifact_title?: string | null;
  iterations: number;
  rag_counts: Record<string, number>;
  stable: boolean;
  mode_rag?: string | null;
}

export interface AceStabilityTestResult {
  review_id: string;
  run_id: string;
  subject_label: string;
  ace_profile: AceProfile;
  iterations_requested: number;
  iterations_completed: number;
  stable: boolean;
  unstable_check_count: number;
  unstable_phase_count: number;
  checks: AceStabilityCheckVariance[];
  phases: AceStabilityPhaseVariance[];
}

export interface AceStabilityTestTaskResponse {
  task_id: string;
  status: string;
  message: string;
}

export interface AceStabilityTestStatus {
  task_id: string;
  status: string;
  progress?: number | null;
  status_message?: string | null;
  error?: string | null;
  ready: boolean;
  result?: AceStabilityTestResult | null;
}

export interface AceStabilityTestHistoryItem {
  task_id: string;
  status: string;
  created_at?: string | null;
  completed_at?: string | null;
  run_id?: string | null;
  subject_label?: string | null;
  ace_profile?: AceProfile | null;
  iterations_requested?: number | null;
  stable?: boolean | null;
  result?: AceStabilityTestResult | null;
  error?: string | null;
}

export interface AceStabilityTestHistoryResponse {
  items: AceStabilityTestHistoryItem[];
}

export type AceProfile = "strict" | "balanced" | "lenient";

export interface AceProfileInfo {
  id: AceProfile;
  label: string;
  description: string;
  is_default: boolean;
}

export interface AceTrialSummary {
  trial_id: string;
  review_id: string;
  criteria_version_id: string;
  criteria_version_label?: string | null;
  ace_profile?: AceProfile;
  created_at: string;
  actor_id: string;
  note?: string;
  is_canonical: boolean;
  orch_rag: Rag | null;
  out_rag: Rag | null;
  run_number: number;
}

export interface AceTrialPhaseDiff {
  phase: "orchestration" | "output";
  baseline_rag: Rag | null;
  compare_rag: Rag | null;
  changed: boolean;
}

export interface AceTrialCheckDiff {
  phase: "orchestration" | "output";
  check_id: string;
  check_label: string;
  baseline_answer: CheckAnswer | null;
  compare_answer: CheckAnswer | null;
  baseline_reason?: string;
  compare_reason?: string;
  changed: boolean;
  only_in_baseline: boolean;
  only_in_compare: boolean;
}

export interface AceTrialRef {
  trial_id: string;
  label: string;
  criteria_version_id: string;
  criteria_version_label?: string | null;
  ace_profile?: AceProfile;
  created_at: string;
  is_canonical: boolean;
  orch_rag: Rag | null;
  out_rag: Rag | null;
  run_number: number;
}

export interface AceTrialCompareResult {
  review_id: string;
  baseline: AceTrialRef;
  compare: AceTrialRef;
  same_criteria_version: boolean;
  same_ace_profile?: boolean;
  headline: string;
  phase_diffs: AceTrialPhaseDiff[];
  check_diffs: AceTrialCheckDiff[];
  total_changes: number;
}

interface RunDetailResponse extends Omit<AgentRun, "steps"> {
  steps: AgentStep[];
}

interface CheckResultPayload {
  id: string;
  severity: "hard" | "soft";
  answer: CheckAnswer;
  comment?: string;
}

interface PhaseRatingPayload {
  step_index: number;
  step_type: "orchestration" | "output";
  step_payload: AgentStep;
  checks: CheckResultPayload[];
  suggested_rag: Rag | null;
  human_rag: Rag | null;
  human_note: string;
}

function checkResults(
  criteria: PhaseCriteria,
  answers: PhaseRatings["orchestration"]["answers"],
  comments: PhaseRatings["orchestration"]["check_comments"] = {},
): CheckResultPayload[] {
  return criteria.checks
    .filter((c) => answers[c.id])
    .map((c) => ({
      id: c.id,
      severity: c.severity,
      answer: answers[c.id],
      comment: answers[c.id] === "fail" ? (comments[c.id]?.trim() ?? "") : "",
    }));
}

function phaseRatingsToPayload(run: AgentRun, ratings: PhaseRatings): PhaseRatingPayload[] {
  const criteria = run.review_criteria;
  const items: PhaseRatingPayload[] = [];

  const orch = firstOrchestrationStep(run);
  items.push({
    step_index: orch?.index ?? 0,
    step_type: "orchestration",
    step_payload: orch?.step ?? { action: "tool_call", thinking: "", tool: "", input: {} },
    checks: criteria ? checkResults(criteria.orchestration, ratings.orchestration.answers, ratings.orchestration.check_comments) : [],
    suggested_rag: ratings.orchestration.suggested_rag,
    human_rag: ratings.orchestration.human_rag,
    human_note: ratings.orchestration.human_note,
  });

  const fin = finishStep(run);
  if (fin) {
    items.push({
      step_index: fin.index,
      step_type: "output",
      step_payload: fin.step,
      checks: criteria ? checkResults(criteria.output, ratings.output.answers, ratings.output.check_comments) : [],
      suggested_rag: ratings.output.suggested_rag,
      human_rag: ratings.output.human_rag,
      human_note: ratings.output.human_note,
    });
  }

  return items;
}

function artefactRatingsToPayload(
  run: AgentRun,
  artefactRatings: ArtefactRatings,
): NonNullable<ReviewRecord["artefact_ratings"]> {
  const artefacts = run.artefacts ?? [];
  if (!artefacts.length) return [];

  return artefacts.map((artefact) => {
    const criteria =
      (artefact.artifact_kind && run.artefact_review_criteria_by_kind?.[artefact.artifact_kind]) ||
      run.artefact_review_criteria;
    const rating = artefactRatings[artefact.artifact_id] ?? {
      answers: {},
      check_comments: {},
      suggested_rag: null,
      human_rag: null,
      human_note: "",
      human_approval: null,
    };
    const checks = criteria
      ? checkResults(criteria, rating.answers, rating.check_comments)
      : Object.entries(rating.answers).map(([id, answer]) => ({
          id,
          severity: "hard" as const,
          answer,
        }));
    return {
      artifact_id: artefact.artifact_id,
      checks,
      suggested_rag: rating.suggested_rag,
      human_rag: rating.human_rag,
      human_note: rating.human_note,
      human_approval: rating.human_approval,
    };
  });
}

export const agentReviewAPI = {
  async listTaskTypes(): Promise<TaskTypeOption[]> {
    return get<TaskTypeOption[]>(`${BASE}/task-types`);
  },

  /** All guided task types for the dashboard picker, plus worker chat when queued. */
  async listReviewTaskTypes(): Promise<TaskTypeOption[]> {
    const [guided, queued] = await Promise.all([
      this.listImportCatalogueOptions(),
      this.listTaskTypes().catch(() => [] as TaskTypeOption[]),
    ]);
    const byKey = new Map<string, TaskTypeOption>();
    for (const option of guided) {
      byKey.set(option.catalogue_key, option);
    }
    for (const option of queued) {
      if (!byKey.has(option.catalogue_key)) {
        byKey.set(option.catalogue_key, option);
      }
    }
    return [...byKey.values()].sort((a, b) => a.display_name.localeCompare(b.display_name));
  },

  async listImportCatalogueOptions(): Promise<TaskTypeOption[]> {
    try {
      return await get<TaskTypeOption[]>(`${BASE}/task-types`, { params: { for_import: true } });
    } catch (err) {
      if (!axios.isAxiosError(err) || err.response?.status !== 404) {
        throw err;
      }
      try {
        return await get<TaskTypeOption[]>(`${BASE}/import/catalogue-options`);
      } catch (fallbackErr) {
        if (!axios.isAxiosError(fallbackErr) || fallbackErr.response?.status !== 404) {
          throw fallbackErr;
        }
        return get<TaskTypeOption[]>(`${BASE}/task-types`);
      }
    }
  },

  async listRuns(catalogueKey?: string, sessionId?: string | null, limit = 100): Promise<RunListItem[]> {
    return get<RunListItem[]>(`${BASE}/runs`, {
      params: {
        ...(catalogueKey ? { catalogue_key: catalogueKey } : {}),
        ...(sessionId ? { session_id: sessionId } : {}),
        limit,
      },
    });
  },

  async getRun(runId: string): Promise<AgentRun> {
    const detail = await get<RunDetailResponse>(`${BASE}/runs/${encodeURIComponent(runId)}`);
    return detail as AgentRun;
  },

  async getReviewForRun(runId: string): Promise<ReviewRecord> {
    return get<ReviewRecord>(`${BASE}/runs/${encodeURIComponent(runId)}/review`);
  },

  async submitReview(args: {
    run: AgentRun;
    ratings: PhaseRatings;
    artefactRatings?: ArtefactRatings;
  }): Promise<ReviewRecord> {
    const { run, ratings, artefactRatings = {} } = args;
    return post<ReviewRecord>(`${BASE}/reviews`, {
      run_id: run.run_id,
      source: run.source,
      task_name: run.task_name,
      ratings: phaseRatingsToPayload(run, ratings),
      artefact_ratings: artefactRatingsToPayload(run, artefactRatings),
    });
  },

  async getReviewComparison(reviewId: string): Promise<ReviewComparison> {
    return get<ReviewComparison>(`${BASE}/reviews/${encodeURIComponent(reviewId)}`);
  },

  async rateWithAce(
    reviewId: string,
    force = false,
    criteriaVersionId?: string | null,
    artefactCount = 0,
  ): Promise<ReviewComparison> {
    return post<ReviewComparison>(`${BASE}/reviews/${encodeURIComponent(reviewId)}/ace-rate`, {}, {
      params: {
        force,
        ...(criteriaVersionId ? { criteria_version_id: criteriaVersionId } : {}),
      },
      timeout: aceRateTimeoutMs(artefactCount),
    });
  },

  async listAceProfiles(): Promise<AceProfileInfo[]> {
    return get<AceProfileInfo[]>(`${BASE}/ace-profiles`);
  },

  async listAceTrials(reviewId: string): Promise<AceTrialSummary[]> {
    return get<AceTrialSummary[]>(`${BASE}/reviews/${encodeURIComponent(reviewId)}/ace-trials`);
  },

  async runAceTrial(
    reviewId: string,
    args: {
      criteriaVersionId?: string | null;
      aceProfile?: AceProfile;
      promote?: boolean;
      note?: string;
      artefactCount?: number;
    },
  ): Promise<ReviewComparison> {
    return post<ReviewComparison>(
      `${BASE}/reviews/${encodeURIComponent(reviewId)}/ace-trials`,
      {
        criteria_version_id: args.criteriaVersionId ?? null,
        ace_profile: args.aceProfile ?? "strict",
        promote: args.promote ?? false,
        note: args.note ?? "",
      },
      { timeout: aceRateTimeoutMs(args.artefactCount ?? 0) },
    );
  },

  async getAceTrialComparison(reviewId: string, trialId: string): Promise<ReviewComparison> {
    return get<ReviewComparison>(
      `${BASE}/reviews/${encodeURIComponent(reviewId)}/ace-trials/${encodeURIComponent(trialId)}`,
    );
  },

  async compareAceTrials(
    reviewId: string,
    baselineTrialId: string,
    compareTrialId: string,
  ): Promise<AceTrialCompareResult> {
    return get<AceTrialCompareResult>(`${BASE}/reviews/${encodeURIComponent(reviewId)}/ace-trials/compare`, {
      params: {
        baseline_trial_id: baselineTrialId,
        compare_trial_id: compareTrialId,
      },
    });
  },

  async promoteAceTrial(reviewId: string, trialId: string): Promise<ReviewComparison> {
    return post<ReviewComparison>(
      `${BASE}/reviews/${encodeURIComponent(reviewId)}/ace-trials/${encodeURIComponent(trialId)}/promote`,
      {},
    );
  },

  async listImportCandidates(catalogueKey: string, limit = 20): Promise<ImportCandidate[]> {
    return get<ImportCandidate[]>(`${BASE}/import/candidates`, {
      params: { catalogue_key: catalogueKey, limit },
    });
  },

  async listChatImportCandidates(limit = 50, userQuery?: string | null): Promise<ChatImportCandidate[]> {
    return get<ChatImportCandidate[]>(`${BASE}/import/chat-candidates`, {
      params: {
        limit,
        ...(userQuery?.trim() ? { user_query: userQuery.trim() } : {}),
      },
    });
  },

  async lookupImport(query: string): Promise<ImportLookupResult> {
    return get<ImportLookupResult>(`${BASE}/import/lookup`, {
      params: { q: query.trim() },
    });
  },

  async importSpecific(interactionIds: string[], sessionId?: string | null): Promise<ImportResult> {
    return post<ImportResult>(`${BASE}/import/specific`, {
      interaction_ids: interactionIds,
      session_id: sessionId ?? null,
    });
  },

  async importChatSpecific(conversationIds: string[], sessionId?: string | null): Promise<ImportResult> {
    return post<ImportResult>(`${BASE}/import/chat-specific`, {
      conversation_ids: conversationIds,
      session_id: sessionId ?? null,
    });
  },

  async importChatRandomSample(args: {
    count: number;
    sessionId?: string | null;
  }): Promise<ImportResult> {
    return post<ImportResult>(
      `${BASE}/import/chat-random-sample`,
      {
        count: args.count,
        session_id: args.sessionId ?? null,
      },
      { timeout: 120_000 },
    );
  },

  async importRandomSample(args: {
    catalogueKey: string;
    count: number;
    useLlm?: boolean;
    sessionId?: string | null;
  }): Promise<ImportResult> {
    return post<ImportResult>(`${BASE}/import/random-sample`, {
      catalogue_key: args.catalogueKey,
      count: args.count,
      use_llm: args.useLlm ?? false,
      session_id: args.sessionId ?? null,
    }, { timeout: 120_000 });
  },

  async clearQueue(catalogueKey: string, sessionId?: string | null): Promise<ClearQueueResult> {
    return post<ClearQueueResult>(`${BASE}/import/clear-queue`, {
      catalogue_key: catalogueKey,
      session_id: sessionId ?? null,
    });
  },

  async getAnalysis(catalogueKey: string, sessionId?: string | null): Promise<ReviewAnalysisResult> {
    return get<ReviewAnalysisResult>(`${BASE}/analysis`, {
      params: {
        catalogue_key: catalogueKey,
        ...(sessionId ? { session_id: sessionId } : {}),
      },
    });
  },

  async ratePendingWithAce(
    catalogueKey: string,
    sessionId?: string | null,
    pendingCount = 1,
  ): Promise<BatchAceRateResult> {
    const timeout = Math.min(Math.max(pendingCount * 180_000, 180_000), 1_800_000);
    return post<BatchAceRateResult>(
      `${BASE}/ace-rate-pending`,
      {},
      {
        params: {
          catalogue_key: catalogueKey,
          ...(sessionId ? { session_id: sessionId } : {}),
        },
        timeout,
      },
    );
  },

  async resolveCalibrationDispute(body: CalibrationResolutionIn): Promise<CalibrationResolutionRecord> {
    return post<CalibrationResolutionRecord>(`${BASE}/calibration-resolutions`, body);
  },

  async clearCalibrationResolution(body: CalibrationResolutionClearIn): Promise<void> {
    await post<void>(`${BASE}/calibration-resolutions/clear`, body);
  },

  async downloadPortfolioFundDetails(portfolioId: string): Promise<void> {
    try {
      const response = await apiRequestWithHeaders<Blob>({
        method: "GET",
        url: `${BASE}/portfolios/${encodeURIComponent(portfolioId)}/fund-details-export`,
        responseType: "blob",
        timeout: 120_000,
      });

      const disposition =
        (response.headers["content-disposition"] as string | undefined) ??
        (response.headers["Content-Disposition"] as string | undefined);
      const filename =
        filenameFromContentDisposition(disposition) ??
        `agent-review-portfolio-funds-${portfolioId}.xlsx`;

      const blob = new Blob([response.data], { type: XLSX_MIME });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      throw new Error(await parseApiErrorMessage(err, "Portfolio fund export failed"));
    }
  },

  async downloadCalibrationXlsx(
    catalogueKey: string,
    sessionId?: string | null,
    sessionName?: string | null,
  ): Promise<void> {
    try {
      const response = await apiRequestWithHeaders<Blob>({
        method: "GET",
        url: `${BASE}/export/calibration-xlsx`,
        params: {
          catalogue_key: catalogueKey,
          ...(sessionId ? { session_id: sessionId } : {}),
        },
        responseType: "blob",
        timeout: 120_000,
      });

      const disposition =
        (response.headers["content-disposition"] as string | undefined) ??
        (response.headers["Content-Disposition"] as string | undefined);
      const filename =
        filenameFromContentDisposition(disposition) ??
        buildCalibrationExportFilename(catalogueKey, sessionName);

      const blob = new Blob([response.data], { type: XLSX_MIME });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      if (axios.isAxiosError(err) && !err.response) {
        throw new Error(
          "Calibration export failed — the server may still be building the file. Restart the API if this keeps happening.",
        );
      }
      throw new Error(await parseApiErrorMessage(err, "Calibration export failed"));
    }
  },

  async listReviewSessions(catalogueKey: string): Promise<ReviewSessionRecord[]> {
    return get<ReviewSessionRecord[]>(`${BASE}/sessions`, {
      params: { catalogue_key: catalogueKey },
    });
  },

  async createReviewSession(catalogueKey: string, name: string): Promise<ReviewSessionRecord> {
    return post<ReviewSessionRecord>(`${BASE}/sessions`, {
      catalogue_key: catalogueKey,
      name,
    });
  },

  async deleteReviewSession(sessionId: string): Promise<DeleteSessionResult> {
    return del<DeleteSessionResult>(`${BASE}/sessions/${encodeURIComponent(sessionId)}`);
  },

  async moveRunToSession(runId: string, sessionId: string): Promise<void> {
    await post(`${BASE}/runs/${encodeURIComponent(runId)}/move-session`, {
      session_id: sessionId,
    });
  },

  async moveRunsToSession(runIds: string[], sessionId: string): Promise<MoveRunsToSessionResult> {
    return post<MoveRunsToSessionResult>(`${BASE}/move-runs`, {
      session_id: sessionId,
      run_ids: runIds,
    });
  },

  async listReviewVersions(reviewId: string): Promise<ReviewVersionEntry[]> {
    return get<ReviewVersionEntry[]>(`${BASE}/reviews/${encodeURIComponent(reviewId)}/versions`);
  },

  async listCriteriaVersions(catalogueKey: string): Promise<CriteriaVersionRecord[]> {
    return get<CriteriaVersionRecord[]>(`${BASE}/criteria-versions`, {
      params: { catalogue_key: catalogueKey },
    });
  },

  async publishCriteriaVersion(catalogueKey: string, label: string): Promise<CriteriaVersionRecord> {
    return post<CriteriaVersionRecord>(`${BASE}/criteria-versions`, {
      catalogue_key: catalogueKey,
      label,
    });
  },

  async downloadResultsXlsx(
    catalogueKey: string,
    sessionId?: string | null,
    sessionName?: string | null,
    options?: ExportResultsOptions,
  ): Promise<void> {
    const exportOptions: ExportResultsOptions = options ?? {
      include_summary: true,
      include_reviews: true,
      include_checks: true,
      include_loa: true,
    };
    try {
      const response = await apiRequestWithHeaders<Blob>({
        method: "GET",
        url: `${BASE}/export/results-xlsx`,
        params: {
          catalogue_key: catalogueKey,
          ...(sessionId ? { session_id: sessionId } : {}),
          include_summary: exportOptions.include_summary,
          include_reviews: exportOptions.include_reviews,
          include_checks: exportOptions.include_checks,
          include_loa: exportOptions.include_loa,
          include_ace_trials: false,
          include_ace_trial_checks: false,
        },
        responseType: "blob",
        timeout: 120_000,
      });

      const disposition =
        (response.headers["content-disposition"] as string | undefined) ??
        (response.headers["Content-Disposition"] as string | undefined);
      const filename =
        filenameFromContentDisposition(disposition) ??
        buildAgentReviewExportFilename(catalogueKey, sessionName);

      const blob = new Blob([response.data], { type: XLSX_MIME });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      if (axios.isAxiosError(err) && !err.response) {
        throw new Error(
          "Export request failed — the server may still be building the file. Restart the API if this keeps happening.",
        );
      }
      throw new Error(await parseApiErrorMessage(err, "Export failed"));
    }
  },

  async startAceComparisonExport(
    catalogueKey: string,
    sessionId: string | null | undefined,
    request: AceComparisonExportRequest,
  ): Promise<AceComparisonExportTaskResponse> {
    return post<AceComparisonExportTaskResponse>(`${BASE}/export/ace-comparisons-xlsx`, {
      catalogue_key: catalogueKey,
      session_id: sessionId ?? null,
      run_ids: request.runIds,
      ace_profiles: request.aceProfiles,
      force: request.force ?? false,
      include_trial_checks: request.includeTrialChecks ?? true,
    });
  },

  async getAceComparisonExportStatus(taskId: string): Promise<AceComparisonExportTaskStatus> {
    return get<AceComparisonExportTaskStatus>(`${BASE}/export/ace-comparisons/tasks/${taskId}`);
  },

  async downloadAceComparisonExportFile(taskId: string): Promise<void> {
    const response = await apiRequestWithHeaders<Blob>({
      method: "GET",
      url: `${BASE}/export/ace-comparisons/tasks/${taskId}/download`,
      responseType: "blob",
      timeout: 120_000,
    });

    const disposition =
      (response.headers["content-disposition"] as string | undefined) ??
      (response.headers["Content-Disposition"] as string | undefined);
    const filename = filenameFromContentDisposition(disposition) ?? "ace_comparisons.xlsx";

    const blob = new Blob([response.data], { type: XLSX_MIME });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  async downloadAceComparisonsXlsx(
    catalogueKey: string,
    sessionId: string | null | undefined,
    _sessionName: string | null | undefined,
    request: AceComparisonExportRequest,
    onProgress?: (status: AceComparisonExportTaskStatus) => void,
  ): Promise<void> {
    const pollMs = 2_000;
    const maxWaitMs = aceComparisonExportTimeoutMs(
      request.runIds.length,
      request.aceProfiles.length,
      catalogueKey,
    );
    const startedAt = Date.now();

    let queued: AceComparisonExportTaskResponse;
    try {
      queued = await this.startAceComparisonExport(catalogueKey, sessionId, request);
    } catch (err) {
      throw new Error(await parseApiErrorMessage(err, "Ace comparison export failed"));
    }

    let taskStatus = await this.getAceComparisonExportStatus(queued.task_id);
    onProgress?.(taskStatus);

    while (!taskStatus.ready && !["failed", "cancelled"].includes(taskStatus.status)) {
      if (Date.now() - startedAt > maxWaitMs) {
        throw new Error(
          "Ace comparison export is still running — check Ops → Task Manager or try fewer cases.",
        );
      }
      await new Promise((resolve) => setTimeout(resolve, pollMs));
      taskStatus = await this.getAceComparisonExportStatus(queued.task_id);
      onProgress?.(taskStatus);
    }

    if (taskStatus.status === "failed" || taskStatus.error) {
      throw new Error(taskStatus.error ?? "Ace comparison export failed");
    }
    if (!taskStatus.ready) {
      throw new Error("Ace comparison export did not finish");
    }

    try {
      await this.downloadAceComparisonExportFile(queued.task_id);
    } catch (err) {
      throw new Error(await parseApiErrorMessage(err, "Ace comparison download failed"));
    }
  },

  async startAceStabilityTest(
    catalogueKey: string,
    sessionId: string | null | undefined,
    request: AceStabilityTestRequest,
  ): Promise<AceStabilityTestTaskResponse> {
    return post<AceStabilityTestTaskResponse>(`${BASE}/ace-stability-test`, {
      catalogue_key: catalogueKey,
      session_id: sessionId ?? null,
      run_id: request.runId,
      ace_profile: request.aceProfile,
      iterations: request.iterations,
    });
  },

  async getAceStabilityTestStatus(taskId: string): Promise<AceStabilityTestStatus> {
    return get<AceStabilityTestStatus>(`${BASE}/ace-stability-test/tasks/${taskId}`);
  },

  async listAceStabilityTestHistory(
    catalogueKey: string,
    sessionId: string | null | undefined,
    limit = 30,
  ): Promise<AceStabilityTestHistoryResponse> {
    const params = new URLSearchParams({
      catalogue_key: catalogueKey,
      limit: String(limit),
    });
    if (sessionId) {
      params.set("session_id", sessionId);
    }
    return get<AceStabilityTestHistoryResponse>(`${BASE}/ace-stability-test/history?${params}`);
  },

  async requeueAceStabilityTest(taskId: string): Promise<{ task_id: string; status: string }> {
    return post<{ task_id: string; status: string }>(
      `${BASE}/ace-stability-test/tasks/${taskId}/requeue`,
      {},
    );
  },
};
